import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { fetchAnalysisDashboard } from '../config/api';
import SeoHead from '../seo/SeoHead';
import { sectorAnalysisMeta } from '../seo/metadata';
import { datasetSchema, breadcrumbSchema } from '../seo/structuredData';

const Analysis = () => {
  const [companyAttacks, setCompanyAttacks] = useState([]);
  const [sectorAttacks, setSectorAttacks] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchAnalysisDashboard();
        setCompanyAttacks(data.companies || []);
        setSectorAttacks(data.sectorSummary || []);
        setTimelineData(data.frequency || []);
      } catch (err) {
        setError('Failed to fetch analysis data');
        console.error('Error fetching analysis data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  // Calculate total attacks from company data
  const totalAttacks = companyAttacks.reduce((sum, company) => {
    return sum + parseInt(company.attack_count || 0);
  }, 0);

  // Calculate unique sectors
  const uniqueSectors = new Set();
  companyAttacks.forEach(company => {
    if (company.sectors && Array.isArray(company.sectors)) {
      company.sectors.forEach(sector => uniqueSectors.add(sector));
    }
  });

  // Prepare pie chart data
  const pieData = [
    { name: 'Critical Risk', value: companyAttacks.filter(c => c.risk_level === 'Critical Risk').length, color: '#DC2626' },
    { name: 'High Risk', value: companyAttacks.filter(c => c.risk_level === 'High Risk').length, color: '#EF4444' },
    { name: 'Medium Risk', value: companyAttacks.filter(c => c.risk_level === 'Medium Risk').length, color: '#F59E0B' },
    { name: 'Low Risk', value: companyAttacks.filter(c => c.risk_level === 'Low Risk').length, color: '#3B82F6' },
    { name: 'Single Attack', value: companyAttacks.filter(c => c.risk_level === 'Single Attack').length, color: '#6B7280' }
  ].filter(item => item.value > 0); // Only show categories with data

  // Prepare timeline data for chart - ensure it's an array
  const chartTimelineData = Array.isArray(timelineData) 
    ? timelineData
        .filter(item => item.period && item.period_label)
        .map(item => ({
          month: item.period_label,
          attack_count: item.attack_count
        }))
        .slice(-24) // Last 24 months
    : [];

  // Calculate max values for proper scaling
  const maxCompanyAttacks = Math.max(...companyAttacks.slice(0, 10).map(c => parseInt(c.attack_count || 0)));
  const maxSectorAttacks = Math.max(...sectorAttacks.slice(0, 10).map(s => parseInt(s.attack_count || 0)));
  const maxTimelineAttacks = Math.max(...chartTimelineData.map(t => parseInt(t.attack_count || 0)));

  const topSector = sectorAttacks[0];
  const seo = sectorAnalysisMeta({
    sector: topSector?.sector,
    reportCount: topSector?.count || topSector?.attack_count,
  });
  const totalSectorReports = sectorAttacks.reduce(
    (sum, s) => sum + parseInt(s.count || s.attack_count || 0, 10),
    0
  );

  return (
    <div className="space-y-6 p-6">
      <SeoHead
        {...seo}
        jsonLd={[
          datasetSchema({
            totalReports: totalSectorReports,
            sectors: sectorAttacks.slice(0, 10).map((s) => s.sector),
          }),
          breadcrumbSchema([
            { name: 'Phalanx', path: '/' },
            { name: 'Analysis', path: '/analysis' },
          ]),
        ]}
      />
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Market Analysis</h1>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="text-2xl font-bold text-blue-600">
            {companyAttacks.length}
          </div>
          <div className="text-gray-600">Companies Analyzed</div>
        </div>
        <div className="card">
          <div className="text-2xl font-bold text-red-600">
            {companyAttacks.filter(c => c.risk_level === 'Critical Risk' || c.risk_level === 'High Risk').length}
          </div>
          <div className="text-gray-600">High Risk Companies</div>
        </div>
        <div className="card">
          <div className="text-2xl font-bold text-green-600">
            {totalAttacks}
          </div>
          <div className="text-gray-600">Total Attacks</div>
        </div>
        <div className="card">
          <div className="text-2xl font-bold text-purple-600">
            {uniqueSectors.size}
          </div>
          <div className="text-gray-600">Sectors Covered</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Targeted Companies */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Targeted Companies</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={companyAttacks.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="company_name" 
                angle={-45}
                textAnchor="end"
                height={100}
                fontSize={12}
              />
              <YAxis 
                domain={[0, Math.ceil(maxCompanyAttacks * 1.1)]}
                tickCount={6}
              />
              <Tooltip 
                formatter={(value, name) => [value, 'Attack Count']}
                labelFormatter={(label) => `Company: ${label}`}
              />
              <Legend />
              <Bar dataKey="attack_count" fill="#3B82F6" name="Attack Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Level Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Level Distribution</h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `${value} companies (${((value / companyAttacks.length) * 100).toFixed(1)}%)`,
                  props.payload.name
                ]}
              />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => (
                  <span style={{ color: entry.color, fontSize: '12px' }}>
                    {value}: {entry.payload.value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sector Attacks */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sector Attack Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sectorAttacks.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="sector" 
                angle={-45}
                textAnchor="end"
                height={100}
                fontSize={12}
              />
              <YAxis 
                domain={[0, Math.ceil(maxSectorAttacks * 1.1)]}
                tickCount={6}
              />
              <Tooltip 
                formatter={(value, name) => [value, 'Attack Count']}
                labelFormatter={(label) => `Sector: ${label}`}
              />
              <Bar dataKey="attack_count" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Timeline */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attack Timeline (Last 24 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartTimelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                angle={-45}
                textAnchor="end"
                height={100}
                fontSize={10}
              />
              <YAxis 
                domain={[0, Math.ceil(maxTimelineAttacks * 1.1)]}
                tickCount={6}
              />
              <Tooltip 
                formatter={(value, name) => [value, 'Attacks']}
                labelFormatter={(label) => `Period: ${label}`}
              />
              <Line type="monotone" dataKey="attack_count" stroke="#8B5CF6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Company List */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">All Companies by Risk Level</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attack Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attackers
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companyAttacks.map((company, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {company.company_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {company.company_tickers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.attack_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      company.risk_level === 'Critical Risk' ? 'bg-red-100 text-red-800' :
                      company.risk_level === 'High Risk' ? 'bg-orange-100 text-orange-800' :
                      company.risk_level === 'Medium Risk' ? 'bg-yellow-100 text-yellow-800' :
                      company.risk_level === 'Low Risk' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {company.risk_level}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {company.attackers}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analysis;
